import { deleteData, getData, postData } from '../../../helpers/axiosHelper';
import { queryString } from '../../../utils/heap';

import { IPaginationParams } from '../../common';
import { ADMIN_ENDPOINT } from '../consts';
import {
  IFetchAccountConnectionDataRequestData,
  IFetchDatastreamsDataRequestData,
  IGetAccountConnectionsResponseData,
  IGetCustomerReportsResponseData,
  IGetDatastreamsResponseData,
  IGetNotApprovedUsersResponseData,
  IGetUsersResponseData,
  IChangeUserStatusRequestData,
  IGetDatastreamFetchStatusRequestData,
  IGetDatastreamFetchStatusResponseData,
  InviteSupplierMarketplaceResponse,
  IGetCustomersWithFeaturesResponse,
  IUpdateCustomersFeaturesResponse,
  IUpdateCustomersFeaturesRequest,
  IGetCustomersWithPagesResponse,
  ICreateCustomPageRequest,
  ICreateCustomPageResponse,
  IEditCustomPageRequest,
  IEditCustomPageResponse,
  IToggleIsSynapseSyncEnabledRequest,
  IToggleIsSynapseSyncEnabledResponse,
  ISetSynapseDatabaseRequest,
  ISetSynapseDatabaseResponse,
  IGetSynapseDatabaseConnectionRequest,
  IGetSynapseDatabaseConnectionResponse,
  IGetSynapseViewsResponse,
  IGetSynapseViewsRequest,
  ISyncSynapseDatabaseViewsRequest,
  ISyncSynapseDatabaseViewsResponse,
  IGetClientsSynapseContainerLinkRequest,
  IGetClientsSynapseContainerLinkResponse,
  IGetCustomPagesFromFolderRequest,
  IGetCustomPagesFromFolderResponse,
  ISyncSynapseDatabaseViewsForAllCustomersRequest,
  IGetCustomersWithSideUsersResponse,
} from '../types';

export const getAccountConnections = async (
  params: Partial<IPaginationParams>
): Promise<IGetAccountConnectionsResponseData> => {
  const url = `${ADMIN_ENDPOINT}/connections/linkedaccounts`;

  return getData(url, params);
};

export const deleteAccountConnection = async (
  accountId: string
): Promise<void> => {
  const url = `${ADMIN_ENDPOINT}/connections/linkedaccount/${accountId}`;
  return deleteData(url);
};

export const getNotApprovedUsers = async (
  params: Partial<IPaginationParams>
): Promise<IGetNotApprovedUsersResponseData> => {
  const url = `${ADMIN_ENDPOINT}/customers/pending-approval`;
  return getData(url, params);
};

export const approveUser = async (customerId: string): Promise<void> => {
  const url = `${ADMIN_ENDPOINT}/customers/${customerId}/approve`;
  return postData(url);
};

export const changeUserStatus = async (
  body: IChangeUserStatusRequestData
): Promise<void> => {
  const url = `${ADMIN_ENDPOINT}/customers/change-status`;
  return postData(url, body);
};

export const getCustomerReports = async (
  params: Partial<IPaginationParams>
): Promise<IGetCustomerReportsResponseData> => {
  const url = `${ADMIN_ENDPOINT}/customer-reports`;
  return getData(url, params);
};

export const getDatastreams =
  async (): Promise<IGetDatastreamsResponseData> => {
    const url = `${ADMIN_ENDPOINT}/datastreams`;
    return getData(url);
  };

export const refreshDatastreams = async (): Promise<void> => {
  const url = `${ADMIN_ENDPOINT}/datastreams-refresh`;
  return postData(url);
};

export const getDatastreamFetchStatus = async (
  queryParams: IGetDatastreamFetchStatusRequestData
): Promise<IGetDatastreamFetchStatusResponseData> => {
  const url = `${ADMIN_ENDPOINT}/datastream-fetch-status`;
  return getData(
    queryString.stringifyUrl({
      url,
      query: {
        ...queryParams,
      },
    })
  );
};

export const fetchDatastreamsData = async (
  body: IFetchDatastreamsDataRequestData
): Promise<void> => {
  const url = `${ADMIN_ENDPOINT}/datastream-data-fetch`;
  return postData(url, body);
};

export const fetchAccountConnectionData = async (
  body: IFetchAccountConnectionDataRequestData
): Promise<void> => {
  const url = `${ADMIN_ENDPOINT}/authorization-data-fetch`;
  return postData(url, body);
};

export const getUsers = async (): Promise<IGetUsersResponseData> => {
  const url = `${ADMIN_ENDPOINT}/user-page`;
  return getData(url);
};

export const inviteSupplierMarketplace = async (
  customerEmail: string
): Promise<InviteSupplierMarketplaceResponse> => {
  const url = `${ADMIN_ENDPOINT}/supplier-marketplace/invitation?customerEmail=${customerEmail}`;
  return getData(url);
};

export const getCustomersWithFeatures = async (
  pageSize: number,
  pageNumber: number
): Promise<IGetCustomersWithFeaturesResponse> => {
  const url = `${ADMIN_ENDPOINT}/features/customers-with-features?PageSize=${pageSize}&PageNumber=${pageNumber}`;
  return getData(url);
};

export const updateCustomersFeatures = async (
  body: IUpdateCustomersFeaturesRequest
): Promise<IUpdateCustomersFeaturesResponse> => {
  const url = `${ADMIN_ENDPOINT}/features/update-customers-features`;
  return postData(url, body.customersWithFeatures);
};

export const getCustomersWithPages =
  async (): Promise<IGetCustomersWithPagesResponse> => {
    const url = `${ADMIN_ENDPOINT}/custom-pages/customers-with-pages`;
    return getData(url);
  };

export const getCustomPagesFromFolder = async (
  queryParams: IGetCustomPagesFromFolderRequest
): Promise<IGetCustomPagesFromFolderResponse> => {
  const url = `${ADMIN_ENDPOINT}/custom-pages/folder-pages`;
  return getData(url, queryParams);
};

export const createCustomPage = async (
  body: ICreateCustomPageRequest
): Promise<ICreateCustomPageResponse> => {
  const url = `${ADMIN_ENDPOINT}/custom-pages/customers-with-pages/add`;
  return postData(url, body);
};

export const editCustomPage = async (
  body: IEditCustomPageRequest
): Promise<IEditCustomPageResponse> => {
  const url = `${ADMIN_ENDPOINT}/custom-pages/customers-with-pages/edit`;
  return postData(url, body);
};

export const deleteCustomPage = async (pageId: string): Promise<void> => {
  const url = `${ADMIN_ENDPOINT}/custom-pages/delete-page/${pageId}`;
  return deleteData(url);
};

export const toggleIsSynapseSyncEnabled = async (
  body: IToggleIsSynapseSyncEnabledRequest
): Promise<IToggleIsSynapseSyncEnabledResponse> => {
  const url = `${ADMIN_ENDPOINT}/synapse-sync/is-enabled`;
  return postData(url, body, undefined, true);
};

export const setSynapseDatabase = async (
  body: ISetSynapseDatabaseRequest
): Promise<ISetSynapseDatabaseResponse> => {
  const url = `${ADMIN_ENDPOINT}/synapse-sync/database`;
  return postData(url, body, undefined, true);
};

export const testSynapseDatabaseConnection = async (
  queryParams: IGetSynapseDatabaseConnectionRequest
): Promise<IGetSynapseDatabaseConnectionResponse> => {
  const url = `${ADMIN_ENDPOINT}/synapse-sync/test-database`;
  return getData(url, queryParams, undefined, true);
};

export const getSynapseDatabaseViews = async (
  queryParams: IGetSynapseViewsRequest
): Promise<IGetSynapseViewsResponse> => {
  const url = `${ADMIN_ENDPOINT}/synapse-sync/views`;
  return getData(url, queryParams, undefined, true);
};

export const syncSynapseDatabaseViews = async (
  body: ISyncSynapseDatabaseViewsRequest
): Promise<ISyncSynapseDatabaseViewsResponse> => {
  const url = `${ADMIN_ENDPOINT}/synapse-sync/sync-views`;
  return postData(url, body, undefined, true);
};

export const syncSynapseDatabaseViewsForAllCustomers = async (
  body: ISyncSynapseDatabaseViewsForAllCustomersRequest
): Promise<ISyncSynapseDatabaseViewsResponse> => {
  const url = `${ADMIN_ENDPOINT}/synapse-sync/sync-views-for-all-customers`;
  return postData(url, body, undefined, true);
};

export const getClientsSynapseContainerLink = async (
  queryParams: IGetClientsSynapseContainerLinkRequest
): Promise<IGetClientsSynapseContainerLinkResponse> => {
  const url = `${ADMIN_ENDPOINT}/synapse-sync/container-link`;
  return getData(url, queryParams);
};

export const getCustomersWithSideUsers =
  async (): Promise<IGetCustomersWithSideUsersResponse> => {
    const url = `${ADMIN_ENDPOINT}/side-users/customers-with-side-users`;
    return getData(url);
  };
